import { useState, useEffect } from 'react';
import { ConfigKeys } from './types/Config';
import { AttributeKey } from './types/ConfigAttribute';
import { api, Config, ConfigAttribute } from './api';

export default function ConfigPage() {
  const [activeTab, setActiveTab] = useState<ConfigKeys>(ConfigKeys.SYSTEM);
  const [configs, setConfigs] = useState<Config[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editedAttributes, setEditedAttributes] = useState<ConfigAttribute[]>([]);

  const getConfigByKey = (key: ConfigKeys) => {
    return configs.find(config => config.key === key);
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const data = await api.getAllConfigs();
      setConfigs(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    const config = getConfigByKey(activeTab);
    setEditedAttributes(config?.attributes || []);
  }, [activeTab, configs]);

  const handleAttributeChange = (index: number, value: string) => {
    const newAttributes = [...editedAttributes];
    newAttributes[index] = { ...newAttributes[index], attribute_value: value };
    setEditedAttributes(newAttributes);
  };

  const handleSaveConfig = async () => {
    try {
      await api.saveConfig({
        key: activeTab,
        name: ConfigKeys[activeTab],
        attributes: editedAttributes,
      });
      await fetchConfigs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const renderConfigForm = (configKey: ConfigKeys) => {
    const config = getConfigByKey(configKey);
    const defaultAttributes = configKey === ConfigKeys.WHATSAPP ? [
      { attribute_key: AttributeKey.ACCOUNT_SID, attribute_name: 'Account SID', attribute_value: '' },
      { attribute_key: AttributeKey.PHONE_NUMBER, attribute_name: 'Phone Number', attribute_value: '' },
    ] : configKey === ConfigKeys.TWITTER ? [
      { attribute_key: AttributeKey.ACCOUNT_SID, attribute_name: 'Account SID', attribute_value: '' },
      { attribute_key: AttributeKey.AUTH_TOKEN, attribute_name: 'Auth Token', attribute_value: '' },
      { attribute_key: AttributeKey.PHONE_NUMBER, attribute_name: 'Phone Number', attribute_value: '' },
      { attribute_key: AttributeKey.TOKEN, attribute_name: 'Token', attribute_value: '' },
    ] : [
      { attribute_key: AttributeKey.ACCESS_KEY, attribute_name: 'Access Key', attribute_value: '' },
      { attribute_key: AttributeKey.ACCOUNT_SID, attribute_name: 'Account SID', attribute_value: '' },
      { attribute_key: AttributeKey.AUTH_TOKEN, attribute_name: 'Auth Token', attribute_value: '' },
      { attribute_key: AttributeKey.PHONE_NUMBER, attribute_name: 'Phone Number', attribute_value: '' },
      { attribute_key: AttributeKey.TOKEN, attribute_name: 'Token', attribute_value: '' },
    ];

    const attributes = config?.attributes || defaultAttributes;

    return (
      <div className="space-y-6">
        {attributes.map((attr, index) => (
          <div key={index} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {attr.attribute_name}
            </label>
            <input
              type="text"
              value={editedAttributes[index]?.attribute_value || ''}
              onChange={(e) => handleAttributeChange(index, e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
              placeholder={`Enter ${attr.attribute_name.toLowerCase()}`}
            />
          </div>
        ))}
        <div className="pt-4 border-t flex justify-end">
          <button
            onClick={handleSaveConfig}
            className="btn-primary"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Configuration
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage system configurations and integrations
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-4 px-6">
              {Object.entries(ConfigKeys)
                .filter(([key]) => isNaN(Number(key)))
                .map(([key, value]) => (
                  <div
                    key={key}
                    onClick={() => setActiveTab(value as ConfigKeys)}
                    className={`px-4 py-3 text-sm transition-colors cursor-pointer ${
                      activeTab === value
                        ? 'text-gray-900 dark:text-white font-bold border-b-2 border-blue-500'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {key}
                  </div>
                ))}
            </nav>
          </div>

          <div className="p-6">
            {renderConfigForm(activeTab)}
          </div>
        </div>
      </div>
    </div>
  );
} 